// ── Texto enriquecido: lo que se pega, lo que se copia (2026-10-20) ──────────
//
// Eduardo: "cuando pego no puedo interpretar cuándo hay un enter y cuándo no;
// todo se ve muy pegado. Y cuando lo copio y lo pego en otro lado quedan dos,
// tres espacios entre párrafos."
//
// Tres causas que se alimentaban entre sí:
// 1. El editor separaba los párrafos con 6px: un Enter no se distinguía de un
//    salto de línea. Para separar, se apretaba Enter dos veces, y eso deja un
//    párrafo VACÍO (medido: 7 de 9 correos nuevos de Villa Sión los tenían).
// 2. Pegar texto convertía cada línea en un párrafo, y pegar desde Gmail, Word
//    o Docs traía sus líneas en blanco como párrafos vacíos.
// 3. Afuera, Gmail y Word le dan a cada párrafo su espacio (1em) — también a
//    los vacíos. Un párrafo vacío entre dos se volvía tres espacios.
//
// El modelo, el de Word: Enter = párrafo nuevo, con su espacio; Shift+Enter =
// salto de línea dentro del párrafo. Los párrafos vacíos no se usan para
// separar. Lo de acá lo aplica RichTextEditor al pegar y al copiar, y todo
// Evo Hub pasa el HTML a texto con `htmlATexto`.

/**
 * El espacio entre párrafos, igual que `--evo-rte-parrafo` en tokens.css. Va
 * escrito en el HTML que se copia porque afuera (Gmail, Word) no hay variables
 * CSS: sin esto, cada programa pone el suyo y no se ve como en Evo Hub.
 */
export const ESPACIO_ENTRE_PARRAFOS = '1em';

const BLOQUES = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'TABLE', 'HR']);

function escapar(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Sin texto visible ni imagen: un párrafo que solo separa. */
function vacio(el: Element): boolean {
  if (el.querySelector('img, hr, table')) return false;
  return (el.textContent ?? '').replace(/[\s ​]/g, '') === '';
}

function tieneBloques(el: Element): boolean {
  return [...el.children].some(c => BLOQUES.has(c.tagName) || c.tagName === 'DIV');
}

/**
 * El HTML que llega al pegar, con párrafos de verdad y sin párrafos vacíos.
 *
 * - Gmail escribe cada línea como un <div> y la línea en blanco como
 *   <div><br></div>: las líneas seguidas son UN párrafo (con saltos de línea) y
 *   la línea en blanco es donde empieza el siguiente.
 * - Word y Docs escriben <p> por párrafo y marcan las líneas en blanco con
 *   párrafos vacíos (<p><o:p>&nbsp;</o:p></p>, <p><br></p>): se descartan.
 */
export function normalizarHtmlPegado(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('style, meta, link, title, script').forEach(n => { n.remove(); });
  // Las etiquetas propias de Word (<o:p>) no son nada: se quedan con su contenido.
  doc.querySelectorAll('o\\:p').forEach(n => { n.replaceWith(...n.childNodes); });

  const salida: string[] = [];
  let lineas: string[] = [];
  const cerrarParrafo = (): void => {
    const conTexto = lineas.filter(l => l.replace(/<br\s*\/?>|&nbsp;|\s/gi, '') !== '');
    if (conTexto.length > 0) salida.push(`<p>${conTexto.join('<br>')}</p>`);
    lineas = [];
  };

  const recorrer = (contenedor: Element): void => {
    let enLinea = '';
    const cerrarLinea = (): void => {
      if (enLinea.trim() !== '') lineas.push(enLinea.trim());
      enLinea = '';
    };
    for (const nodo of [...contenedor.childNodes]) {
      if (nodo.nodeType === Node.TEXT_NODE) {
        enLinea += escapar(nodo.textContent ?? '');
        continue;
      }
      if (nodo.nodeType !== Node.ELEMENT_NODE) continue;
      const el = nodo as Element;
      if (el.tagName === 'BR') {
        // Un <br> suelto entre líneas: salto de línea; dos seguidos, párrafo nuevo.
        if (enLinea.trim() === '') { cerrarLinea(); cerrarParrafo(); } else cerrarLinea();
        continue;
      }
      if (el.tagName === 'DIV') {
        cerrarLinea();
        if (tieneBloques(el)) { recorrer(el); continue; }
        // Una línea de Gmail. Vacía: es la línea en blanco que separa párrafos.
        if (vacio(el)) cerrarParrafo();
        else lineas.push(el.innerHTML.trim());
        continue;
      }
      if (BLOQUES.has(el.tagName)) {
        cerrarLinea();
        cerrarParrafo();
        if (el.tagName === 'P') {
          if (!vacio(el)) salida.push(`<p>${el.innerHTML.trim()}</p>`);
        } else if (!(el.tagName !== 'HR' && el.tagName !== 'TABLE' && vacio(el))) {
          salida.push(el.outerHTML);
        }
        continue;
      }
      // En línea (span, a, b, i…): parte de la línea en curso.
      enLinea += el.outerHTML;
    }
    cerrarLinea();
  };

  recorrer(doc.body);
  cerrarParrafo();
  return salida.join('');
}

/**
 * Texto plano a párrafos, como en Word: una línea en blanco separa párrafos y
 * un salto suelto es un salto de línea dentro del párrafo. Antes cada línea
 * era un párrafo y no se distinguía una cosa de la otra.
 */
export function textoPlanoAParrafosHtml(texto: string): string {
  return texto
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t ]*\n+/)
    .map(b => b.replace(/^\n+|\n+$/g, ''))
    .filter(b => b.trim() !== '')
    .map(b => `<p>${b.split('\n').map(escapar).join('<br>')}</p>`)
    .join('');
}

/**
 * HTML a texto plano, para todo Evo Hub: un párrafo es una línea en blanco, un
 * salto de línea es un salto, una lista lleva viñetas. Nunca más de una línea
 * en blanco seguida, aunque el HTML traiga párrafos vacíos o líneas con solo
 * espacios.
 */
export function htmlATexto(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|blockquote|pre|ul|ol)>/gi, '\n\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .split('\n').map(l => l.replace(/[ \t ]+$/g, '')).join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Nada que mandar: sin texto y sin imagen. Un correo con solo una captura sí tiene contenido. */
export function cuerpoVacio(html: string): boolean {
  return !/<img\b/i.test(html) && htmlATexto(html) === '';
}

/**
 * Al copiar o cortar texto enriquecido de Evo Hub: lo que va al portapapeles
 * se ve afuera como acá (ver `htmlParaCopiar`), y el texto plano lleva una
 * sola línea en blanco entre párrafos.
 *
 * En el editor, ProseMirror ya puso su versión en el portapapeles (su handler
 * corre antes que este): se toma ésa y se reescribe. En un texto de solo
 * lectura no hay nada puesto: se arma con lo seleccionado.
 */
export function alCopiarTextoEnriquecido(e: { clipboardData: DataTransfer; preventDefault: () => void }): void {
  let html = e.clipboardData.getData('text/html');
  if (!html) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const caja = document.createElement('div');
    caja.appendChild(sel.getRangeAt(0).cloneContents());
    html = caja.innerHTML;
  }
  e.clipboardData.setData('text/html', htmlParaCopiar(html));
  e.clipboardData.setData('text/plain', htmlATexto(html));
  e.preventDefault();
}

/**
 * El HTML que sale al copiar: cada párrafo con el mismo espacio que tiene en
 * Evo Hub, escrito en el párrafo, y sin párrafos vacíos. Así Gmail o Word lo
 * muestran como se ve acá, sin sumar su espacio al de un párrafo vacío.
 */
export function htmlParaCopiar(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  doc.body.querySelectorAll('p').forEach(p => {
    if (vacio(p) && !p.closest('li')) { p.remove(); return; }
    p.setAttribute('style', `margin:0 0 ${ESPACIO_ENTRE_PARRAFOS}`);
  });
  doc.body.querySelectorAll('li > p').forEach(p => { p.setAttribute('style', 'margin:0'); });
  doc.body.querySelectorAll('ul, ol').forEach(l => { l.setAttribute('style', `margin:0 0 ${ESPACIO_ENTRE_PARRAFOS}`); });
  return doc.body.innerHTML;
}
